/**
 * The streamed answer as the overlay and the results page draw it (`KLQ-20260905-001` §5a).
 *
 * **Append-only, by construction.** A group is its lexical hits in the order they were sent,
 * then its semantic hits under the "Related" divider. Nothing that arrives later is inserted
 * above anything already drawn, which is what lets keyboard selection be held by hit key: the
 * selected row never changes identity, and it never moves because a row landed above it.
 */

import { SEARCH_GROUPS } from './api';
import type { SearchGroupKey, SearchHit } from './api';
import type { SearchStreamState } from './useSearchStream';

/**
 * The groups a semantic event can add to. Nodes, projects and people have no vectors (§1,
 * Rule V and Rule P; `SearchSemanticEvent`'s docstring), so a skeleton row under them would be
 * a promise nothing keeps.
 */
const SEMANTIC_GROUPS: ReadonlySet<SearchGroupKey> = new Set(['drawing', 'task', 'comment']);

/**
 * How many hits a group carries in the first answer. TWIN: `OVERLAY_PER_GROUP` in
 * `backend/src/kalqa/domains/search/query.py`. A group that came back full may have more; one
 * that did not, has not — which is the only way to know without a count (§5a: no totals).
 */
export const PER_GROUP = 5;

export interface GroupView {
  readonly group: SearchGroupKey;
  readonly lexical: readonly SearchHit[];
  readonly semantic: readonly SearchHit[];
  /** Related hits may still be appended here: draw exactly one skeleton row under the group. */
  readonly pending: boolean;
}

/** Unique across groups; an id alone is unique too, but the key says where the row lives. */
export function hitKey(hit: SearchHit): string {
  return `${hit.group}:${hit.id}`;
}

/**
 * The groups to draw, in brief 06's order. A group is drawn when it has hits, or while related
 * hits may still arrive in it — so a comment group that only meaning can fill has its place
 * held under a skeleton row rather than appearing from nowhere between two others.
 */
export function groupViews(state: SearchStreamState): readonly GroupView[] {
  const { lexical, semantic } = state;
  if (lexical === null) return [];
  const pending = state.phase === 'semantic';
  const views: GroupView[] = [];
  for (const group of SEARCH_GROUPS) {
    const sent = lexical[group];
    const seen = new Set(sent.map(hitKey));
    // The server already excludes every lexical id (phase 4, Notes item 5); held here too, so a
    // row can never be drawn twice whatever a later server does.
    const related = (semantic?.[group] ?? []).filter((hit) => !seen.has(hitKey(hit)));
    const waiting = pending && SEMANTIC_GROUPS.has(group);
    if (sent.length > 0 || related.length > 0 || waiting) {
      views.push({ group, lexical: sent, semantic: related, pending: waiting });
    }
  }
  return views;
}

/** Every hit in drawing order — what ↑ and ↓ walk. */
export function flatHits(views: readonly GroupView[]): readonly SearchHit[] {
  return views.flatMap((view) => [...view.lexical, ...view.semantic]);
}

/**
 * Which row is selected: the one the person chose while it is still on screen, otherwise the
 * first exact match. Never "the first row", which a related hit appended to an earlier group
 * would change under the person's cursor.
 */
export function selectedKey(
  views: readonly GroupView[],
  chosen: string | null,
): string | null {
  const hits = flatHits(views);
  if (chosen !== null && hits.some((hit) => hitKey(hit) === chosen)) return chosen;
  const firstExact = views.find((view) => view.lexical.length > 0)?.lexical[0];
  if (firstExact !== undefined) return hitKey(firstExact);
  const first = hits[0];
  return first === undefined ? null : hitKey(first);
}

/** Whether a finished answer found nothing at all. */
export function isEmptyAnswer(state: SearchStreamState, views: readonly GroupView[]): boolean {
  // While semantic is pending, `groupViews` holds a place for every group meaning can fill, so
  // an empty list in that phase means the caller filtered to a group it cannot fill (nodes,
  // projects, people) — already final, not still loading.
  const settled = state.phase === 'done' || state.phase === 'semantic';
  return settled && state.problem === null && views.length === 0;
}
